import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Trash2, Loader2, AlertCircle, ShieldOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import Footer from '../components/Footer';

const API_URL =
  process.env.REACT_APP_ACCOUNT_DELETION_URL ||
  'https://eztfytmvkufcsimtbkmo.supabase.co/functions/v1/account-deletion-web';

const DeleteAccountConfirm = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirect to /delete-account if no token provided
  useEffect(() => {
    if (!token) {
      navigate('/delete-account', { replace: true });
    }
  }, [token, navigate]);

  const handleConfirm = async () => {
    setError('');
    setLoading(true);

    try {
      const res = await fetch(API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({ token }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => null);
        throw new Error(data?.error || 'The confirmation link is invalid or has expired.');
      }

      // Clear token from URL and redirect to success page
      navigate('/delete-account/scheduled', { replace: true });
    } catch (err) {
      console.error('Account deletion confirmation error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) return null;

  return (
    <div className="min-h-screen bg-white flex flex-col">
        <link rel="canonical" href="https://www.lingu-pix.app/delete-account/confirm" />
        <title>Confirm Deletion – LinguPix</title>
        <meta name="robots" content="noindex, nofollow" />
        <meta name="referrer" content="no-referrer" />

      {/* Header */}
      <header className="bg-[#333333] text-white py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/delete-account"
            className="inline-flex items-center text-[#FFA55A] hover:text-white transition-colors mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </Link>
          <h1 className="text-4xl font-bold">Confirm Account Deletion</h1>
          <p className="text-gray-300 mt-2">
            Final step before your account is scheduled for deletion
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-1 flex items-start justify-center">
        <div className="bg-white border-2 border-[#FF4444]/20 rounded-2xl p-8 text-center space-y-6 w-full max-w-xl">
          <div className="w-20 h-20 bg-[#FF4444]/10 rounded-full flex items-center justify-center mx-auto">
            <Trash2 size={40} className="text-[#FF4444]" />
          </div>

          <h2 className="text-2xl font-bold text-[#333333]">
            Confirm account deletion
          </h2>

          <div className="space-y-3 max-w-lg mx-auto">
            <p className="text-[#666666] leading-relaxed">
              You are about to schedule deletion for your LinguPix account.
              Access will be revoked immediately after confirmation.
            </p>
          </div>

          {/* Warning box */}
          <div className="bg-[#FFF3E0] border border-[#FFE5CC] rounded-xl p-4 flex items-start space-x-3 text-left max-w-md mx-auto">
            <ShieldOff size={20} className="text-[#FF7F30] flex-shrink-0 mt-0.5" />
            <p className="text-[#333333] text-sm leading-relaxed">
              This action cannot be undone after the 30-day grace period.
              During the grace period, you can contact us to cancel the deletion.
            </p>
          </div>

          {error && (
            <div className="bg-[#FF4444]/10 border border-[#FF4444] rounded-xl p-4 flex items-start space-x-3 max-w-md mx-auto text-left">
              <AlertCircle size={20} className="text-[#FF4444] flex-shrink-0 mt-0.5" />
              <p className="text-[#FF4444] text-sm">{error}</p>
            </div>
          )}

          <Button
            onClick={handleConfirm}
            disabled={loading}
            className="bg-[#FF4444] hover:bg-[#CC3333] text-white font-semibold h-12 px-8 text-lg"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 animate-spin" size={20} />
                Confirming...
              </>
            ) : (
              'Confirm account deletion'
            )}
          </Button>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default DeleteAccountConfirm;
